import { Ref, Dispatch } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslate } from 'src/locales';
import { CallListPersonType, CallListEntryDetail } from 'src/services/call-lists/call-lists.types';

import Iconify from 'src/components/iconify';

import { personName } from '../utils';
import { previewFor } from '../call-outcome';
import OutcomeButtons from './outcome-buttons';
import NextCallPicker from './next-call-picker';
import CreateDealButton from './create-deal-button';
import { CurrentCallState, CurrentCallAction } from '../hooks/use-current-call';

// ----------------------------------------------------------------------

type Props = {
  entry: CallListEntryDetail;
  state: CurrentCallState;
  dispatch: Dispatch<CurrentCallAction>;
  /** Seconds on the call, for the "Call ended" line. */
  seconds: number;
  /** Called as notes are typed, so a caller can keep a draft. */
  onNotesChange?: (notes: string) => void;
  /** The outcome block, so the caller can bring it into view when a call ends. */
  outcomeRef?: Ref<HTMLDivElement>;
};

/**
 * Logging a call (JT1-1827): how it went, notes for the profile, and the next step. The next step is
 * the next call; anything else to do is an action, saved as a To-do task. Used by the current-call
 * panel on the call list and by the overlay that opens after calling from a profile.
 */
export default function CallOutcomeForm({ entry, state, dispatch, seconds, onNotesChange, outcomeRef }: Props) {
  const { t } = useTranslate();
  const { person } = entry;
  const firstName = person.first_name || personName(person);
  const saving = state.phase === 'saving';

  return (
    <Stack spacing={3}>
      <Stack spacing={1.5} ref={outcomeRef} data-testid="call-outcome">
        {state.phase === 'after_call' && state.startedAt !== null && (
          <Alert severity="success" icon={<Iconify icon="solar:phone-calling-bold" />} data-testid="call-ended">
            {t('call_lists.panel.call_ended', { duration: `${Math.floor(seconds / 60)} min ${seconds % 60} sec` })}
          </Alert>
        )}

        <Typography variant="subtitle2">{t('call_lists.outcome')}</Typography>
        <OutcomeButtons
          value={state.outcome}
          disabled={saving}
          onChange={(outcome) => dispatch({ type: 'SET_OUTCOME', outcome, at: Date.now() })}
        />
        {/* JT1-1827: a good call can turn straight into a deal, linked to the contact and company */}
        {person.type === CallListPersonType.CONTACT && (
          <Box>
            <CreateDealButton contactId={person.id} contactName={personName(person)} company={entry.company} />
          </Box>
        )}
      </Stack>

      <TextField
        multiline
        minRows={3}
        label={t('call_lists.call_notes')}
        placeholder={t('call_lists.call_notes_placeholder', { name: firstName })}
        value={state.notes}
        onChange={(event) => {
          dispatch({ type: 'SET_NOTES', notes: event.target.value });
          onNotesChange?.(event.target.value);
        }}
        inputProps={{ maxLength: 10000 }}
      />

      <Stack spacing={1.5} data-testid="call-next-step">
        <Typography variant="subtitle2">{t('call_lists.next_step')}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: 'text.secondary' }}>
          <Iconify icon="solar:phone-bold" width={16} />
          <Typography variant="body2" color="inherit">{t('call_lists.next_call.title')}</Typography>
        </Stack>
        <NextCallPicker
          outcome={state.outcome}
          preview={previewFor(entry, state.outcome)}
          value={state.nextCall}
          onChange={(nextCall) => dispatch({ type: 'SET_NEXT_CALL', nextCall })}
          disabled={saving}
        />

        <FormControlLabel
          control={(
            <Checkbox
              checked={!!state.followUp}
              disabled={saving}
              onChange={(event) => dispatch({ type: 'SET_FOLLOW_UP', followUp: event.target.checked ? { title: '', dueAt: null } : null })}
            />
          )}
          label={(
            <Stack>
              <Typography variant="body2">{t('call_lists.follow_up.title')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('call_lists.follow_up.caption', { name: firstName })}</Typography>
            </Stack>
          )}
        />
        {state.followUp && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label={t('call_lists.follow_up.task')}
              value={state.followUp.title}
              onChange={(event) => dispatch({ type: 'SET_FOLLOW_UP', followUp: { title: event.target.value, dueAt: state.followUp?.dueAt ?? null } })}
              inputProps={{ maxLength: 255 }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label={t('call_lists.follow_up.due')}
              InputLabelProps={{ shrink: true }}
              value={state.followUp.dueAt ?? ''}
              onChange={(event) => dispatch({ type: 'SET_FOLLOW_UP', followUp: { title: state.followUp?.title ?? '', dueAt: event.target.value || null } })}
            />
          </Stack>
        )}
      </Stack>

      {state.error && <Alert severity="error">{state.error}</Alert>}
    </Stack>
  );
}
